import {
  NextBestAction,
  type Opportunity,
  OpportunityStatusEnum,
  StagesEnum,
} from "@repo/api";
import {
  type IconType,
  calendarIcon,
  clipboardPencilIcon,
  documentAddIcon,
  moneyCashIcon,
  pencilIcon,
  personIcon,
  phoneIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";

interface NextBestActionItemConfig {
  title: string;
  caption: string;
  icon: IconType;
}

export enum RECOMMENDED_ACTION_IDS {
  FILES_SECTION = "files-section",
  INSTALLATION_QUOTATION_SECTION = "installation-quotation-section",
  ORDERS_SECTION = "orders-section",
}

export const nextBestActionConfig: Record<
  NextBestAction,
  NextBestActionItemConfig
> = {
  [NextBestAction.ASSIGN_PROJECT]: {
    title: t("action.assign_the_project"),
    caption: t("prompt.delegate_project_to_a_coworker"),
    icon: personIcon,
  },
  [NextBestAction.SCHEDULE_APPOINTMENT]: {
    title: t("action.schedule_an_appointment"),
    caption: t("prompt.book_a_time_with_the_client"),
    icon: calendarIcon,
  },
  [NextBestAction.PRE_CALL]: {
    title: t("action.pre_call"),
    caption: t("prompt.prepare_before_meeting"),
    icon: phoneIcon,
  },
  [NextBestAction.NEED_ANALYSIS_QUESTIONNAIRE]: {
    title: t("label.need_analysis_questionnaire"),
    caption: t("prompt.gather_client_requirements"),
    icon: clipboardPencilIcon,
  },
  [NextBestAction.ATTACH_PLANNER]: {
    title: t("action.attach_planner_drawing"),
    caption: t("prompt.upload_planner_files"),
    icon: pencilIcon,
  },
  [NextBestAction.BOOK_FOLLOW_UP_APPOINTMENT]: {
    title: t("action.book_follow_up_appointment"),
    caption: t("prompt.set_up_a_future_check_in"),
    icon: calendarIcon,
  },
  [NextBestAction.ATTACH_QUOTE_OR_ORDER]: {
    title: t("action.attach_quote_or_order"),
    caption: t("prompt.include_quote_shopping_list_or_draft"),
    icon: pencilIcon,
  },
  [NextBestAction.ATTACH_INSTALLATION_OR_SERVICE]: {
    title: t("action.attach_installation_or_service"),
    caption: t("prompt.add_related_documents"),
    icon: pencilIcon,
  },
  [NextBestAction.FOLLOW_UP]: {
    title: t("action.follow_up_with_client"),
    caption: t("prompt.reach_out_for_updates"),
    icon: calendarIcon,
  },
  [NextBestAction.UPLOAD_FILES]: {
    title: t("action.upload_files"),
    caption: t("prompt.attach_necessary_documents"),
    icon: documentAddIcon,
  },
  [NextBestAction.UPDATE_BUDGET]: {
    title: t("action.update_budget"),
    caption: t("prompt.revise_project_budget_details"),
    icon: moneyCashIcon,
  },
  [NextBestAction.UPDATE_EXPECTED_PURCHASE_DATE]: {
    title: t("action.update_expected_purchase_date"),
    caption: t("prompt.adjust_project_timeline"),
    icon: documentAddIcon,
  },
};

export const isProjectInactive = (opportunity: Opportunity) =>
  opportunity.status === OpportunityStatusEnum.Invalid ||
  opportunity.status === OpportunityStatusEnum.ClosedLost ||
  (opportunity.status === OpportunityStatusEnum.ClosedWon &&
    !opportunity.stageData) ||
  opportunity.stageData?.stage === StagesEnum.COMPLETED;
